import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BankTransactionSuccessCard from "@/components/ui/bankPayment/BankTransactionSuccessCard";
import BankTransactionErrorCard from "@/components/ui/bankPayment/BankTransactionErrorCard";
import { BankTransactionResult, BankPaymentData, ForeeCallbackResponse } from "@/types";

const PaymentSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get success data from navigation state
  const successData = location.state;

  // Check if this is a Foree payment response
  const isForeePayment = successData?.foree_response;
  const foreeResponse = successData?.foree_response as ForeeCallbackResponse;

  // If no success data is provided, show error
  if (!successData || (!successData.transaction_id && !isForeePayment)) {
    return (
      <BankTransactionErrorCard
        title="Invalid Data"
        message="Payment success information is missing. Please try again from the app."
      />
    );
  }

  // Create result and data objects from the success data
  if (isForeePayment && foreeResponse) {
    // Handle Foree payment response
    const result: BankTransactionResult = {
      id: foreeResponse.transaction_ref_id || foreeResponse.reference_number,
      status: foreeResponse.status === "1" ? "SUCCESS" : "FAILED",
      result: foreeResponse.status === "1" ? "SUCCESS" : "FAILED",
      amount: foreeResponse.paid_amount?.toString() || "0",
    };

    const responseData: BankPaymentData = {
      transaction_id: foreeResponse.transaction_ref_id || foreeResponse.reference_number,
      order_id: foreeResponse.reference_number,
      acquirer_name: foreeResponse.instrument_institution || "Foree",
      transaction_type: "PAYMENT",
      status: foreeResponse.status === "1" ? "SUCCESS" : "FAILED",
      result: foreeResponse.status === "1" ? "SUCCESS" : "FAILED",
      source: "FOREE_PAYMENT",
      receipt_number: foreeResponse.reference_number,
      amount: foreeResponse.paid_amount?.toString() || "0",
      booking_id: "unknown",
      date: foreeResponse.transaction_date_time ? foreeResponse.transaction_date_time.split(' ')[0] : new Date().toISOString().split('T')[0],
      time: foreeResponse.transaction_date_time ? foreeResponse.transaction_date_time.split(' ')[1] : new Date().toTimeString().split(' ')[0],
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-auto">
          <BankTransactionSuccessCard result={result} data={responseData} />
        </div>
      </div>
    );
  } else {
    // Handle regular bank payment response
    const result: BankTransactionResult = {
      id: successData.transaction_id || "unknown",
      status: "SUCCESS",
      result: "SUCCESS",
      amount: "0", // You may need to get this from the API response
    };

    const responseData: BankPaymentData = {
      transaction_id: successData.transaction_id,
      order_id: "unknown", // You may need to get this from the API response
      acquirer_name: "Bank",
      transaction_type: "PAYMENT",
      status: "SUCCESS",
      result: "SUCCESS",
      source: "BANK_PAYMENT",
      receipt_number: successData.transaction_id,
      amount: "0", // You may need to get this from the API response
      booking_id: "unknown", // You may need to get this from the API response
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().split(' ')[0],
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-auto">
          <BankTransactionSuccessCard result={result} data={responseData} />
        </div>
      </div>
    );
  }
};

export default PaymentSuccessPage;
